<template>
  <div class="fb-page">
    <el-card shadow="never" class="header-card">
      <div class="page-header">
        <div>
          <h2>任务结果</h2>
          <p>查看已提交广告的创建结果和投放信息。导入 Excel 前会校验重复的系列名称。</p>
        </div>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-download" :loading="downloadLoading" @click="handleDownloadTemplate">
            下载模板
          </el-button>
          <el-button size="small" icon="el-icon-refresh" :loading="refreshLoading" @click="refreshStatus">
            刷新状态
          </el-button>
          <el-button size="small" icon="el-icon-document" :loading="exportLoading" @click="handleExport">
            导出 Excel
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" :loading="importLoading" @click="triggerImport">
            创建广告
          </el-button>
          <input
            ref="excelInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="hidden-input"
            @change="handleExcelChange"
          />
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <div class="toolbar-left">
          <span class="table-title">广告列表</span>
          <el-select
            v-model="filterAccountId"
            size="small"
            clearable
            placeholder="全部 Account"
            style="width: 280px"
            @change="handleFilter"
          >
            <el-option label="全部 Account" value="" />
            <el-option
              v-for="item in accounts"
              :key="item.accountId"
              :label="accountOptionLabel(item)"
              :value="item.accountId"
            />
          </el-select>
          <span class="total-text">共 {{ total }} 条</span>
        </div>
      </div>

      <el-table
        v-loading="tableLoading"
        :data="tableData"
        border
        stripe
        size="small"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="90" align="center" />
        <el-table-column label="Account ID" width="160" align="center">
          <template slot-scope="scope">
            {{ displayAccountId(scope.row.accountId) }}
          </template>
        </el-table-column>
        <el-table-column prop="campaignName" label="广告系列名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="广告目标" width="120" align="center">
          <template slot-scope="scope">
            {{ mapObjectiveLabel(scope.row.objective) }}
          </template>
        </el-table-column>
        <el-table-column label="成效目标" width="140" align="center">
          <template slot-scope="scope">
            {{ mapOptimizationLabel(scope.row.optimizationGoal) }}
          </template>
        </el-table-column>
        <el-table-column label="预算" width="130" align="center">
          <template slot-scope="scope">
            {{ scope.row.dailyBudget == null ? '-' : formatUsd(scope.row.dailyBudget) + '/日' }}
          </template>
        </el-table-column>
        <el-table-column label="单次成效费用目标" width="150" align="center">
          <template slot-scope="scope">
            {{ formatUsd(scope.row.bidAmount) }}
          </template>
        </el-table-column>
        <el-table-column label="落地页链接" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <a
              v-if="scope.row.finalUrl"
              :href="scope.row.finalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="link-text"
            >
              {{ scope.row.finalUrl }}
            </a>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="导入时间" width="170" align="center" />
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="scope">
            <span class="status-pill" :class="'is-' + mapAdStatus(scope.row.importStatus).type">
              {{ mapAdStatus(scope.row.importStatus).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="statusDesc" label="状态说明" min-width="220" show-overflow-tooltip />
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
  import {
    IMPORT_STATUS,
    accountOptionLabel,
    displayAccountId,
    downloadAdTemplate,
    exportAds,
    extractImportCampaignNames,
    formatUsd,
    importAds,
    isAdPending,
    listAccounts,
    listAds,
    mapAdStatus,
    mapObjectiveLabel,
    mapOptimizationLabel
  } from '@/api/facebookAds'
  import { downloadExcel, parseTableFile } from '@/utils/excel'

  export default {
    name: 'FBCreateAd',
    data() {
      return {
        accounts: [],
        filterAccountId: '',
        tableData: [],
        tableLoading: false,
        currentPage: 1,
        pageSize: 20,
        total: 0,
        downloadLoading: false,
        refreshLoading: false,
        exportLoading: false,
        importLoading: false,
        pollTimer: null
      }
    },
    mounted() {
      this.initPage()
    },
    beforeDestroy() {
      this.clearPoll()
    },
    methods: {
      accountOptionLabel,
      displayAccountId,
      formatUsd,
      mapAdStatus,
      mapObjectiveLabel,
      mapOptimizationLabel,
      async initPage() {
        await this.loadAccounts()
        await this.loadData()
      },
      async loadAccounts() {
        try {
          const response = await listAccounts()
          this.accounts = response.body.data || []
        } catch (error) {
          this.$message.error(error.message || '加载 Account 失败')
        }
      },
      async loadData(silent = false) {
        if (!silent) this.tableLoading = true
        try {
          const response = await listAds({
            accountId: this.filterAccountId,
            pageNum: this.currentPage,
            pageSize: this.pageSize
          })
          this.tableData = Array.isArray(response.body && response.body.data) ? response.body.data : []
          this.total = (response.body && response.body.total) || 0
          this.setupPoll()
        } catch (error) {
          this.$message.error(error.message || '加载广告失败')
        } finally {
          if (!silent) this.tableLoading = false
        }
      },
      handleFilter() {
        this.currentPage = 1
        this.loadData()
      },
      async refreshStatus() {
        this.refreshLoading = true
        try {
          await this.loadData()
          this.$message.success('状态已刷新')
        } finally {
          this.refreshLoading = false
        }
      },
      async handleDownloadTemplate() {
        this.downloadLoading = true
        try {
          await downloadAdTemplate()
          this.$message.success('模板已开始下载')
        } catch (error) {
          this.$message.error(error.message || '模板下载失败')
        } finally {
          this.downloadLoading = false
        }
      },
      async handleExport() {
        this.exportLoading = true
        try {
          const result = await exportAds({ accountId: this.filterAccountId })
          if (result.mode === 'empty') {
            await this.exportCurrentList()
          }
          this.$message.success('导出成功')
        } catch (error) {
          try {
            await this.exportCurrentList()
            this.$message.success('导出成功')
          } catch (fallbackError) {
            this.$message.error(error.message || '导出失败')
          }
        } finally {
          this.exportLoading = false
        }
      },
      async exportCurrentList() {
        const response = await listAds({
          accountId: this.filterAccountId,
          pageNum: 1,
          pageSize: 10000
        })
        const rows = (Array.isArray(response.body && response.body.data) ? response.body.data : []).map((item) => [
          item.id,
          item.accountId,
          item.campaignName,
          item.objective,
          item.optimizationGoal,
          item.campaignDailyBudget,
          item.bidAmountCents,
          item.finalUrl,
          item.uploadTime,
          this.mapAdStatus(item.importStatus).label,
          item.statusDesc
        ])
        downloadExcel('Facebook广告列表.xlsx', [
          {
            name: '广告列表',
            header: ['主键ID', '广告账户ID', '系列名称', '营销目标', '优化目标', '系列日预算（美分）', '出价金额（美分）', '落地页URL', '创建时间', '导入状态', '错误信息'],
            rows
          }
        ])
      },
      triggerImport() {
        this.$refs.excelInput.value = ''
        this.$refs.excelInput.click()
      },
      findDuplicateNames(names) {
        const count = {}
        names.forEach((name) => {
          count[name] = (count[name] || 0) + 1
        })
        return Object.keys(count).filter((name) => count[name] > 1)
      },
      async handleExcelChange(event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return

        const isExcel =
          /\.(xlsx|xls|csv)$/i.test(file.name) ||
          [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
          ].includes(file.type)
        if (!isExcel) {
          this.$message.error('请上传 Excel 文件')
          return
        }
        if (file.size / 1024 / 1024 > 10) {
          this.$message.error('文件大小不能超过 10MB')
          return
        }

        this.importLoading = true
        try {
          try {
            const rawRows = await parseTableFile(file)
            const importNames = extractImportCampaignNames(rawRows)
            const fileDup = this.findDuplicateNames(importNames)
            if (fileDup.length) {
              this.$message.error(`Excel 中系列名称重复：${fileDup.join('、')}`)
              return
            }

            const existingRes = await listAds({
              accountId: this.filterAccountId,
              pageNum: 1,
              pageSize: 10000
            })
            const existingNames = new Set(
              (Array.isArray(existingRes.body && existingRes.body.data) ? existingRes.body.data : [])
                .filter((item) => item.importStatus !== IMPORT_STATUS.FAILED)
                .map((item) => item.campaignName)
                .filter(Boolean)
            )
            const existedDup = importNames.filter((name) => existingNames.has(name))
            if (existedDup.length) {
              this.$message.error(`系列名称已存在：${Array.from(new Set(existedDup)).join('、')}`)
              return
            }
          } catch (parseError) {
            this.$message.warning('无法预校验 Excel 内容，将直接提交给服务端')
          }

          await this.$confirm('确认导入该 Excel 并创建广告吗？', '创建广告', {
            confirmButtonText: '确定导入',
            cancelButtonText: '取消',
            type: 'warning'
          })

          const result = await importAds(file)
          const count = result && result.body && result.body.data
          this.$message.success(count != null ? `已提交创建任务，保存 ${count} 条` : '已提交创建任务')
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          if (error !== 'cancel') {
            this.$message.error(error.message || '导入失败')
          }
        } finally {
          this.importLoading = false
          event.target.value = ''
        }
      },
      handleSizeChange(val) {
        this.pageSize = val
        this.currentPage = 1
        this.loadData()
      },
      handleCurrentChange(val) {
        this.currentPage = val
        this.loadData()
      },
      setupPoll() {
        this.clearPoll()
        const hasPending = this.tableData.some((item) => isAdPending(item.importStatus))
        if (!hasPending) return
        this.pollTimer = setInterval(() => {
          this.loadData(true)
        }, 4000)
      },
      clearPoll() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      }
    }
  }
</script>

<style scoped>
  .fb-page {
    min-height: calc(100vh - 60px);
    padding: 20px;
    background: #f5f7fa;
  }

  .header-card,
  .table-card {
    border-radius: 8px;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .page-header h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .page-header p {
    margin: 0;
    color: #909399;
    font-size: 13px;
    line-height: 1.6;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  .hidden-input {
    display: none;
  }

  .table-card {
    margin-top: 16px;
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .table-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .total-text {
    color: #909399;
    font-size: 13px;
  }

  .link-text {
    color: #409eff;
    text-decoration: none;
  }

  .link-text:hover {
    text-decoration: underline;
  }

  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  .status-pill {
    display: inline-block;
    min-width: 64px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid;
    line-height: 1.6;
  }

  .status-pill.is-success {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
  }

  .status-pill.is-warning {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
  }

  .status-pill.is-danger {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
  }

  .status-pill.is-info {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
  }

  @media (max-width: 768px) {
    .fb-page {
      padding: 12px;
    }

    .page-header {
      flex-direction: column;
    }

    .pagination-container {
      justify-content: center;
    }
  }
</style>
